/**
 * The route table.
 *
 * Only routes that exist are listed. The scope document names 27, and the rest
 * land here as they are built — a route rendering "coming soon" is worse than
 * no route, because it tells a user the feature is one click away when it is
 * not.
 */

import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppShell } from './AppShell';
import { RequireAuth } from '../auth/RequireAuth';
import { RequireRole } from '../auth/RequireRole';
import { SignInPage } from '../auth/SignInPage';
import { SignInCallbackPage } from '../auth/SignInCallbackPage';
import { SsoCompletePage, SsoErrorPage } from '../auth/SsoPages';
import { SelectOrgPage } from '../features/auth/SelectOrgPage';
import { PolicyDetailPage } from '../features/policies/PolicyDetailPage';
import { PolicyLibrary, PolicyLibraryIndex } from '../features/policies/PolicyLibrary';
import { UploadPage } from '../features/policies/UploadPage';
import { ScopeListPage } from '../features/scopeOfService/ScopeListPage';
import { ScopeDetailPage } from '../features/scopeOfService/ScopeDetailPage';
import { ScopeEditPage } from '../features/scopeOfService/ScopeEditPage';
import { ExceptionListPage } from '../features/exceptions/ExceptionListPage';
import { ExceptionDetailPage } from '../features/exceptions/ExceptionDetailPage';
import { ExceptionEditPage } from '../features/exceptions/ExceptionEditPage';
import { CompetencyListPage } from '../features/competencies/CompetencyListPage';
import { MyCompetenciesPage } from '../features/competencies/MyCompetenciesPage';
import { CompetencyDetailPage } from '../features/competencies/CompetencyDetailPage';
import { CompetencyEditPage } from '../features/competencies/CompetencyEditPage';
import { EvidencePage } from '../features/competencies/EvidencePage';
import { FormListPage } from '../features/forms/FormListPage';
import { MyFormsPage } from '../features/forms/MyFormsPage';
import { FormDetailPage } from '../features/forms/FormDetailPage';
import { FormEditPage } from '../features/forms/FormEditPage';
import { CompletePage } from '../features/forms/CompletePage';
import { SubmissionPage } from '../features/forms/SubmissionPage';
import { SearchResultsPage } from '../features/search/SearchResultsPage';

import { SettingsPage } from '../features/settings/SettingsPage';
import { LibraryImportPage, LibraryAttestationImportPage } from '../features/admin/LibraryImportPage';
import { EmailChangeConfirmPage } from '../features/settings/EmailChangeConfirmPage';
import { IntakePage } from '../features/intake/IntakePage';
import { MyTasksPage } from '../features/acknowledgements/MyTasksPage';
import { AcknowledgePage } from '../features/acknowledgements/AcknowledgePage';
import { AdminAcknowledgementsPage } from '../features/admin/AdminAcknowledgementsPage';
import { AckReportPage } from '../features/admin/AckReportPage';
import { OverduePage } from '../features/admin/OverduePage';
import { UsersPage } from '../features/admin/UsersPage';
import { TaxonomyPage } from '../features/admin/TaxonomyPage';
import { AuditPage } from '../features/admin/AuditPage';
import { DataExportPage } from '../features/admin/DataExportPage';
import { OrgSettingsPage } from '../features/admin/OrgSettingsPage';
import { RequireVisibility } from '../auth/RequireVisibility';
import { MyApprovalsPage } from '../features/approvals/MyApprovalsPage';
import { ApprovalWorkflowsPage } from '../features/approvals/ApprovalWorkflowsPage';
import { ApprovalWorkflowDetailPage } from '../features/approvals/ApprovalWorkflowDetailPage';
import { NewApprovalWorkflowPage } from '../features/approvals/NewApprovalWorkflowPage';
import { ApprovalInboxPage } from '../features/approvals/ApprovalInboxPage';
import { HelpCenterPage } from '../features/help/HelpCenterPage';
import { HelpArticlePage } from '../features/help/HelpArticlePage';
import { ConsoleShell } from '../features/console/ConsoleShell';
import { OrganizationsPage } from '../features/console/OrganizationsPage';
import { NewOrganizationPage } from '../features/console/NewOrganizationPage';
import { SystemEmailPage } from '../features/console/SystemEmailPage';
import { SysAdminsPage } from '../features/console/SysAdminsPage';
import { RunbookPage } from '../features/console/RunbookPage';
import { GoLivePage } from '../features/console/GoLivePage';
import { OnboardingPage } from '../features/console/onboarding/OnboardingPage';
import { ImportPage } from '../features/console/ImportPage';
import { AttestationImportPage } from '../features/console/AttestationImportPage';
import { RequestsPage } from '../features/console/RequestsPage';
import { NoAccessPage, NotAuthorizedPage, NotFoundPage } from './DeadEndPages';
import { Spinner } from './Spinner';

/**
 * The document viewer, loaded only when a document is opened.
 *
 * pdf.js is roughly two thirds of the application's JavaScript. Bundling it
 * into the entry point would make every page — including the sign-in screen —
 * wait for a library most sessions never use.
 */
const DocumentViewPage = lazy(() =>
  import('../features/policies/DocumentViewPage').then((m) => ({
    default: m.DocumentViewPage,
  })),
);

export const router = createBrowserRouter([
  { path: '/signin', element: <SignInPage /> },
  // Shape fixed by the deployed backend, which emails `${APP_URL}/signin/${token}`.
  { path: '/signin/:token', element: <SignInCallbackPage /> },
  // Where an identity provider sends people back (functions/src/sso).
  { path: '/sso/complete', element: <SsoCompletePage /> },
  { path: '/sso/error', element: <SsoErrorPage /> },
  // The link sent to a NEW address to confirm an email change; works signed out.
  { path: '/settings/email/:token', element: <EmailChangeConfirmPage /> },
  // The customer's own intake form. No account: the token IS the credential,
  // and it reaches one organization's contact details and nothing else.
  { path: '/welcome/:token', element: <IntakePage /> },

  // Outside the shell: someone with no membership has no navigation to show.
  { path: '/no-access', element: <NoAccessPage /> },

  {
    path: '/',
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      // Home is what you owe, not the whole library. A nurse opens this to
      // find out whether she is behind on anything.
      { index: true, element: <MyTasksPage /> },
      { path: 'search', element: <SearchResultsPage /> },

      // The policy library frame. A layout route with NO PATH of its own, so
      // it can wrap two sibling paths — the list and one policy — without
      // either URL changing. See PolicyLibrary.tsx for why that mattered.
      // The frame decides whether to show both panes; on `/policies` it is
      // just the list, and on a policy with no working list behind it, just
      // the policy.
      {
        element: <PolicyLibrary />,
        children: [
          { path: 'policies', element: <PolicyLibraryIndex /> },
          { path: 'policy/:policyId', element: <PolicyDetailPage /> },
        ],
      },
      {
        path: 'policy/:policyId/view',
        element: (
          <Suspense fallback={<Spinner label="Opening the document" />}>
            <DocumentViewPage />
          </Suspense>
        ),
      },
      {
        path: 'policies/upload',
        element: <RequireRole role="author"><UploadPage /></RequireRole>,
      },
      { path: 'acknowledge/:ackId/:policyId', element: <AcknowledgePage /> },
      { path: 'my-approvals', element: <MyApprovalsPage /> },

      // Scope of service — a paid add-on. The pages themselves show a locked
      // state when the organization has it off; the sidebar item is absent.
      // Reading needs no role (every member); creating and editing is an
      // author's work, and the backend re-checks both.
      { path: 'scope-of-service', element: <ScopeListPage /> },
      {
        path: 'scope-of-service/new',
        element: <RequireRole role="author"><ScopeEditPage /></RequireRole>,
      },
      { path: 'scope-of-service/:scopeId', element: <ScopeDetailPage /> },
      {
        path: 'scope-of-service/:scopeId/edit',
        element: <RequireRole role="author"><ScopeEditPage /></RequireRole>,
      },
      // Exceptions and variances — the same shape, the same gate.
      { path: 'exceptions', element: <ExceptionListPage /> },
      { path: 'exceptions/new', element: <RequireRole role="author"><ExceptionEditPage /></RequireRole> },
      { path: 'exceptions/:exceptionId', element: <ExceptionDetailPage /> },
      { path: 'exceptions/:exceptionId/edit', element: <RequireRole role="author"><ExceptionEditPage /></RequireRole> },
      // Competency and training. `mine` and `assignments/:id` are a person's
      // own; the rest is the catalog. Fixed paths come before the id route.
      { path: 'competencies', element: <CompetencyListPage /> },
      { path: 'competencies/mine', element: <MyCompetenciesPage /> },
      { path: 'competencies/new', element: <RequireRole role="author"><CompetencyEditPage /></RequireRole> },
      { path: 'competencies/assignments/:assignmentId', element: <EvidencePage /> },
      { path: 'competencies/:competencyId', element: <CompetencyDetailPage /> },
      { path: 'competencies/:competencyId/edit', element: <RequireRole role="author"><CompetencyEditPage /></RequireRole> },
      // Forms and attestations. Fixed paths before the id route.
      { path: 'forms', element: <FormListPage /> },
      { path: 'forms/mine', element: <MyFormsPage /> },
      { path: 'forms/new', element: <RequireRole role="author"><FormEditPage /></RequireRole> },
      { path: 'forms/assignments/:assignmentId', element: <CompletePage /> },
      { path: 'forms/submissions/:submissionId', element: <SubmissionPage /> },
      { path: 'forms/:formId', element: <FormDetailPage /> },
      { path: 'forms/:formId/edit', element: <RequireRole role="author"><FormEditPage /></RequireRole> },
      { path: 'help', element: <HelpCenterPage /> },
      { path: 'help/article/:slug', element: <HelpArticlePage /> },
      { path: 'select-org', element: <SelectOrgPage /> },
      { path: 'settings', element: <SettingsPage /> },
      // The old address of the same page.
      { path: 'profile', element: <Navigate to="/settings" replace /> },
      // Admin. Guarded here so a wrong URL lands on an explanation rather
      // than on a page that renders empty because every query was refused.
      {
        path: 'admin/acknowledgements',
        element: <RequireVisibility setting="acknowledgementReports"><AdminAcknowledgementsPage /></RequireVisibility>,
      },
      {
        path: 'admin/acknowledgements/:policyId',
        element: <RequireVisibility setting="acknowledgementReports"><AckReportPage /></RequireVisibility>,
      },
      {
        path: 'admin/approval-inbox',
        element: <RequireRole role="admin"><ApprovalInboxPage /></RequireRole>,
      },
      {
        path: 'admin/approval-workflows',
        element: <RequireRole role="admin"><ApprovalWorkflowsPage /></RequireRole>,
      },
      {
        path: 'admin/approval-workflows/new',
        element: <RequireRole role="admin"><NewApprovalWorkflowPage /></RequireRole>,
      },
      {
        path: 'admin/approval-workflows/:workflowId',
        element: <RequireRole role="admin"><ApprovalWorkflowDetailPage /></RequireRole>,
      },
      {
        path: 'admin/org-settings',
        element: <RequireRole role="admin"><OrgSettingsPage /></RequireRole>,
      },
      {
        path: 'admin/audit',
        element: <RequireRole role="admin"><AuditPage /></RequireRole>,
      },
      // Section 6 of the subscription agreement: the customer's own export of
      // their own organization. The callable takes the organization from the
      // verified session, so this gate is the presentation of that rule.
      {
        path: 'admin/export',
        element: <RequireRole role="admin"><DataExportPage /></RequireRole>,
      },
      {
        path: 'admin/taxonomy',
        element: <RequireRole role="admin"><TaxonomyPage /></RequireRole>,
      },
      {
        path: 'admin/users',
        element: <RequireRole role="admin"><UsersPage /></RequireRole>,
      },
      // The organization's own System Admin brings a whole library in. The
      // second factor is already asked for by RequireAuth for this role.
      {
        path: 'admin/import',
        element: <RequireRole role="sysadmin"><LibraryImportPage /></RequireRole>,
      },
      // Who attested to what, from the system being left; the same person runs it.
      {
        path: 'admin/import/attestations',
        element: <RequireRole role="sysadmin"><LibraryAttestationImportPage /></RequireRole>,
      },
      {
        path: 'admin/overdue',
        element: <RequireVisibility setting="acknowledgementReports"><OverduePage /></RequireVisibility>,
      },

      { path: 'not-authorized', element: <NotAuthorizedPage /> },
    ],
  },

  // The sysadmin console. Its own shell, its own sign-in, and no customer
  // branding — somebody who can see every hospital's data should never be in
  // doubt about which mode they are in. Served at console.standingdocs.com; the
  // separation is presentation, and the claim is what actually decides.
  {
    path: '/console',
    element: <ConsoleShell />,
    children: [
      { index: true, element: <OrganizationsPage /> },
      { path: 'orgs', element: <OrganizationsPage /> },
      { path: 'orgs/new', element: <NewOrganizationPage /> },
      /*
       * The onboarding workflow IS the organization page. The old page lived
       * at orgs/:orgId/legacy for one release and was removed on 2026-10-01,
       * after its last three controls (reminders, paperwork, removing a
       * contact) moved onto the stages.
       */
      { path: 'orgs/:orgId', element: <OnboardingPage /> },
      { path: 'orgs/:orgId/import', element: <ImportPage /> },
      { path: 'orgs/:orgId/import/attestations', element: <AttestationImportPage /> },
      { path: 'requests', element: <RequestsPage /> },
      { path: 'sysadmins', element: <SysAdminsPage /> },
      { path: 'settings', element: <SystemEmailPage /> },
      /**
       * Operations. A child of `/console`, so `ConsoleShell` has already
       * refused anybody without `session.platformSysAdmin` and asked for the
       * second factor before this renders. No new gate, and typing the URL
       * meets the same one the navigation item sits behind.
       */
      // The checklist is what somebody running a go-live actually wants;
      // the prose is a tab across from it.
      // Customer help articles, read from inside the console: a console page
      // that has one (the library import) links here, because the help center
      // itself belongs to the customer application.
      { path: 'help/:slug', element: <HelpArticlePage back={{ to: '/console', label: 'Back to the console' }} /> },
      { path: 'operations', element: <Navigate to="/console/operations/go-live" replace /> },
      { path: 'operations/go-live', element: <GoLivePage /> },
      { path: 'operations/runbook', element: <RunbookPage docId="runbook" /> },
      { path: 'operations/disaster-recovery', element: <RunbookPage docId="disaster-recovery" /> },
      { path: 'operations/platform', element: <RunbookPage docId="platform" /> },
    ],
  },

  { path: '*', element: <NotFoundPage /> },
]);

export { RequireRole };
